import { Suspense, useMemo, useRef, useEffect, useCallback } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Environment, ContactShadows } from '@react-three/drei';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';
import { isWebGLAvailable } from '@/utils/webgl';
import { useShop } from '@/contexts/ShopContext';
import type { GameState, Position } from '@/types/game';
import BoardScene from './BoardScene';

interface GameBoard3DProps {
  gameState: GameState;
  hintPath?: Position[] | null;
  hintPathHazards?: Set<string>;
  onCellClick?: (position: Position) => void;
  onMovementLockChange?: (isLocked: boolean) => void;
  onExitAnimationComplete?: () => void;
  restartCounter?: number;
}

function WebGLFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        color: '#777',
        fontSize: 18,
        fontFamily: 'monospace',
        textAlign: 'center',
        padding: 32,
      }}
    >
      WebGL is not available on this device. Please use a browser with WebGL support to play the 3D version.
    </div>
  );
}

function LoadingFallback() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        color: '#777',
        fontSize: 14,
        fontFamily: 'monospace',
      }}
    >
      Loading 3D scene...
    </div>
  );
}

export type GraphicsQuality = 'high' | 'medium' | 'low';

export default function GameBoard3D({
  gameState,
  hintPath,
  hintPathHazards,
  onCellClick,
  onMovementLockChange,
  onExitAnimationComplete,
  restartCounter,
}: GameBoard3DProps) {
  const webglAvailable = useMemo(() => isWebGLAvailable(), []);
  const { shopState } = useShop();
  const equippedCharacterId = shopState.equippedCharacter || 'default_hero';
  const equippedProps = shopState.equippedProps || [];
  const exitTriggeredRef = useRef(false);
  const prevLevelRef = useRef(gameState.level);
  const prevRestartRef = useRef(restartCounter);

  // Keep latest callbacks in refs to avoid stale closures in setTimeout
  const onExitAnimationCompleteRef = useRef(onExitAnimationComplete);
  onExitAnimationCompleteRef.current = onExitAnimationComplete;
  const onMovementLockChangeRef = useRef(onMovementLockChange);
  onMovementLockChangeRef.current = onMovementLockChange;

  // Derive quality settings from game settings
  const graphics: GraphicsQuality = gameState.gameSettings?.graphics || 'high';
  const effects = gameState.gameSettings?.effects ?? true;

  const dpr: [number, number] = graphics === 'low' ? [1, 1] : graphics === 'medium' ? [1, 1.25] : [1, 1.5];
  const enableShadows = graphics !== 'low';
  const enableBloom = effects && graphics !== 'low';
  const enableEnvironment = graphics !== 'low';
  const enableContactShadows = graphics === 'high' && effects;

  // Reset exit trigger when level changes or restarts
  useEffect(() => {
    if (gameState.level !== prevLevelRef.current || restartCounter !== prevRestartRef.current) {
      exitTriggeredRef.current = false;
      prevLevelRef.current = gameState.level;
      prevRestartRef.current = restartCounter;
    }
  }, [gameState.level, restartCounter]);

  // Detect when player reaches exit tile by watching isPlaying transition
  const prevIsPlayingRef = useRef(gameState.isPlaying);
  useEffect(() => {
    const wasPlaying = prevIsPlayingRef.current;
    prevIsPlayingRef.current = gameState.isPlaying;

    if (wasPlaying && !gameState.isPlaying && gameState.end && !exitTriggeredRef.current) {
      const atEnd = gameState.position[0] === gameState.end[0] &&
        gameState.position[1] === gameState.end[1];

      if (atEnd) {
        exitTriggeredRef.current = true;
        onMovementLockChangeRef.current?.(true);
        // Use refs to avoid stale closure: by the time this fires,
        // pendingLevelComplete will be set in triggerLevelCompleteModal
        setTimeout(() => {
          onMovementLockChangeRef.current?.(false);
          onExitAnimationCompleteRef.current?.();
        }, 3000);
      }
    }
  }, [gameState.isPlaying, gameState.position, gameState.end]);

  if (!webglAvailable) {
    return <WebGLFallback />;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1,
        touchAction: 'none',
      }}
    >
      <Suspense fallback={<LoadingFallback />}>
        <Canvas
          dpr={dpr}
          camera={{
            position: [0, 15, 15],
            fov: 45,
            near: 0.1,
            far: 100,
          }}
          gl={{
            antialias: graphics !== 'low',
            alpha: false,
            powerPreference: 'high-performance',
            stencil: false,
            depth: true,
          }}
          shadows={enableShadows}
          frameloop="always"
          style={{ background: '#020205' }}
        >
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            enableRotate={true}
            minPolarAngle={Math.PI * 0.15}
            maxPolarAngle={Math.PI * 0.45}
            touches={{ ONE: undefined as unknown as THREE.TOUCH, TWO: THREE.TOUCH.ROTATE }}
            mouseButtons={{ LEFT: THREE.MOUSE.ROTATE, MIDDLE: undefined as unknown as THREE.MOUSE, RIGHT: undefined as unknown as THREE.MOUSE }}
            rotateSpeed={0.5}
            enableDamping
            dampingFactor={0.08}
          />

          <BoardScene
            gameState={gameState}
            equippedCharacterId={equippedCharacterId}
            equippedProps={equippedProps}
            hintPath={hintPath}
            hintPathHazards={hintPathHazards}
            onCellClick={onCellClick}
            graphics={graphics}
          />

          {/* Environment reflections for metallic/physical materials */}
          {enableEnvironment && <Environment preset="city" />}

          {/* Soft contact shadows on the ground (high quality only) */}
          {enableContactShadows && (
            <ContactShadows
              position={[0, -0.01, 0]}
              opacity={0.4}
              scale={30}
              blur={2}
              far={4}
              color="#000000"
            />
          )}

          {/* Post-processing: subtle Bloom glow on emissive materials */}
          {enableBloom && (
            <EffectComposer>
              <Bloom
                luminanceThreshold={0.8}
                mipmapBlur
                intensity={0.2}
              />
            </EffectComposer>
          )}
        </Canvas>
      </Suspense>
    </div>
  );
}
